import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { Col, InputNumber, Row } from 'antd'
import {
    SettingOutlined,
    ShoppingOutlined,
    ShoppingCartOutlined,
    PictureOutlined,
    UnorderedListOutlined,
    SafetyCertificateOutlined,
    EyeOutlined,
    NotificationOutlined,
    TeamOutlined,
    ShopOutlined,
    ClockCircleOutlined,
    NumberOutlined,
    FontSizeOutlined,
} from '@ant-design/icons'
import OptionTypesController from '../../../components/options/optionTypes.controller'
import OptionValuesController from '../../../components/options/optionValues.controller'
import { SectionBadge, DimGroup, ToggleField, FieldBlock } from '../../../components/SectionCard'
import { Titles } from '../../../layouts/Titles'
import i from '../../../translations/i'
import { REVIEW_PLANS, planAllows } from './reviewPlans'

const KEYS = [
    'reviewsProductEnabled', 'reviewsProductModeration', 'reviewsProductOnlyAfterPurchase',
    'reviewsOrderEnabled', 'reviewsOrderModeration', 'reviewsOrderPopupEnabled',
    'reviewsFeedEnabled', 'reviewsFeedModeration', 'reviewsFeedOnlyCustomers',
    'reviewsFeedSourceShop', 'reviewsFeedSourceProduct', 'reviewsFeedSourceOrder',
    'reviewsCommentMaxLength', 'reviewsPhotoMaxCount', 'reviewsPhotoDeleteDays',
]

/**
 * До первого onChangeValue в items лежит объект опции с бэка, после — уже само
 * значение. Приводим к числу в обоих случаях, иначе переключатель на первом
 * рендере считает объект за «включено».
 */
const asNumber = (raw: any, fallback: number = 0): number => {
    if (raw === null || raw === undefined) return fallback
    if (typeof raw === 'object') return Number(raw.value_int ?? fallback)

    return Number(raw)
}

const useSeed = (type: any) => {
    useEffect(() => {
        if (!type) return
        OptionValuesController.onChangeValue(type.key, asNumber(OptionValuesController.items[type.key], Number(type.default ?? 0)))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
}

const findType = (key: string) => OptionTypesController.items.find((el: any) => el.key === key)

const isOn = (key: string) => asNumber(OptionValuesController.items[key]) === 1

const Hint = ({ children }: any) => <div style={{ fontSize: 12, color: 'var(--text-muted-color)' }}>{children}</div>

/**
 * plan — с какого тарифа доступен переключатель. Ниже порога он заблокирован и
 * показан выключенным: бэкенд такой тип всё равно не включит, даже если в
 * настройках осталось «вкл» с прошлого тарифа.
 */
const BoolOption = observer(({ optionKey, icon, disabled = false, plan }: any) => {
    const type = findType(optionKey)
    useSeed(type)

    if (!type) return null

    const allowed = plan === undefined || planAllows(plan)

    return (
        <ToggleField
            icon={icon}
            label={type.label}
            checked={allowed && asNumber(OptionValuesController.items[optionKey], Number(type.default ?? 0)) === 1}
            onChange={(v: boolean) => OptionValuesController.onChangeValue(optionKey, v)}
            disabled={disabled}
            plan={plan}
        />
    )
})

const NumberOption = observer(({ optionKey, icon, hint, min = 0 }: any) => {
    const type = findType(optionKey)
    useSeed(type)

    if (!type) return null

    return (
        <FieldBlock icon={icon} label={type.label}>
            <InputNumber
                min={min}
                style={{ width: '100%' }}
                value={asNumber(OptionValuesController.items[optionKey], Number(type.default ?? min))}
                onChange={(v: any) => OptionValuesController.onChangeValue(optionKey, v ?? min)}
            />
            {hint && <div style={{ fontSize: 12, color: 'var(--text-muted-color)', marginTop: 6 }}>{hint}</div>}
        </FieldBlock>
    )
})

const Card = ({ icon, title, background, children }: any) => (
    <div className="order__block" style={{ width: '100%', height: 'auto', alignSelf: 'flex-start' }}>
        <SectionBadge icon={icon} text={title} background={background} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
            {children}
        </div>
    </div>
)

export const ReviewsSettings = observer(() => {

    useEffect(() => {
        OptionTypesController.get([], KEYS)
            .then((keys: string[]) => OptionValuesController.get(keys))
    }, [])

    const productEnabled = isOn('reviewsProductEnabled') && planAllows(REVIEW_PLANS.product)
    const orderEnabled = isOn('reviewsOrderEnabled') && planAllows(REVIEW_PLANS.order)
    const feedEnabled = isOn('reviewsFeedEnabled') && planAllows(REVIEW_PLANS.feed)
    const anyEnabled = productEnabled || orderEnabled || feedEnabled

    if (!OptionTypesController.render || !OptionValuesController.render) {
        return <Titles title={i.t('reviews:settings.title')} breadcrumb={[
            { title: i.t('def:settings'), href: '/settings' },
            { title: i.t('reviews:settings.title') },
        ]} />
    }

    return (
        <form onSubmit={(e) => OptionValuesController.save(e)}>
            <Titles
                title={i.t('reviews:settings.title')}
                buttons={[{ title: i.t('def:actions.save'), type: 'primary', htmlType: 'submit' }]}
                breadcrumb={[
                    { title: i.t('def:settings'), href: '/settings' },
                    { title: i.t('reviews:settings.title') },
                ]}
            />

            <Row gutter={[20, 20]}>
                <Col span={24} xl={12}>
                    <Card
                        icon={<SettingOutlined />}
                        title={i.t('reviews:settings.common')}
                        background="linear-gradient(135deg, var(--primary-color), #9186e0)"
                    >
                        <DimGroup active={anyEnabled}>
                            <NumberOption optionKey="reviewsCommentMaxLength" icon={<FontSizeOutlined />} />
                        </DimGroup>
                    </Card>
                </Col>

                <Col span={24} xl={12}>
                    <Card
                        icon={<ShoppingOutlined />}
                        title={i.t('reviews:types.product')}
                        background="linear-gradient(135deg, #3f3d56, #63607f)"
                    >
                        <BoolOption optionKey="reviewsProductEnabled" icon={<ShoppingOutlined />} plan={REVIEW_PLANS.product} />
                        <Hint>{i.t('reviews:settings.typeHint')}</Hint>

                        <DimGroup active={productEnabled}>
                            <BoolOption optionKey="reviewsProductModeration" icon={<SafetyCertificateOutlined />} disabled={!productEnabled} />
                            <BoolOption optionKey="reviewsProductOnlyAfterPurchase" icon={<ShoppingCartOutlined />} disabled={!productEnabled} />
                        </DimGroup>
                    </Card>
                </Col>

                <Col span={24} xl={12}>
                    <Card
                        icon={<ShoppingCartOutlined />}
                        title={i.t('reviews:types.order')}
                        background="linear-gradient(135deg, #2f6f5e, #4c9b85)"
                    >
                        <BoolOption optionKey="reviewsOrderEnabled" icon={<ShoppingCartOutlined />} plan={REVIEW_PLANS.order} />
                        <Hint>{i.t('reviews:settings.typeHint')}</Hint>

                        <DimGroup active={orderEnabled}>
                            <BoolOption optionKey="reviewsOrderModeration" icon={<SafetyCertificateOutlined />} disabled={!orderEnabled} />
                            <BoolOption optionKey="reviewsOrderPopupEnabled" icon={<NotificationOutlined />} disabled={!orderEnabled} />
                            <Hint>{i.t('reviews:settings.popupHint')}</Hint>
                        </DimGroup>
                    </Card>
                </Col>

                <Col span={24} xl={12}>
                    <Card
                        icon={<UnorderedListOutlined />}
                        title={i.t('reviews:settings.feed')}
                        background="linear-gradient(135deg, #8a5310, #c08234)"
                    >
                        <BoolOption optionKey="reviewsFeedEnabled" icon={<UnorderedListOutlined />} plan={REVIEW_PLANS.feed} />
                        <Hint>{i.t('reviews:settings.feedHint')}</Hint>

                        <DimGroup active={feedEnabled}>
                            <BoolOption optionKey="reviewsFeedModeration" icon={<SafetyCertificateOutlined />} disabled={!feedEnabled} />
                            <BoolOption optionKey="reviewsFeedOnlyCustomers" icon={<TeamOutlined />} disabled={!feedEnabled} />

                            <FieldBlock icon={<EyeOutlined />} label={i.t('reviews:settings.sources')}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
                                    <BoolOption optionKey="reviewsFeedSourceShop" icon={<ShopOutlined />} disabled={!feedEnabled} />
                                    <BoolOption optionKey="reviewsFeedSourceProduct" icon={<ShoppingOutlined />} disabled={!feedEnabled || !productEnabled} />
                                    <BoolOption optionKey="reviewsFeedSourceOrder" icon={<ShoppingCartOutlined />} disabled={!feedEnabled || !orderEnabled} />
                                    {(!productEnabled || !orderEnabled) && <Hint>{i.t('reviews:settings.sourcesHint')}</Hint>}
                                </div>
                            </FieldBlock>
                        </DimGroup>
                    </Card>
                </Col>

                <Col span={24} xl={12}>
                    <Card
                        icon={<PictureOutlined />}
                        title={i.t('reviews:settings.photos')}
                        background="linear-gradient(135deg, #34526e, #567f9e)"
                    >
                        <DimGroup active={anyEnabled}>
                            <NumberOption optionKey="reviewsPhotoMaxCount" icon={<NumberOutlined />} />
                            <NumberOption
                                optionKey="reviewsPhotoDeleteDays"
                                icon={<ClockCircleOutlined />}
                                min={1}
                                hint={i.t('reviews:settings.photoDeleteHint')}
                            />
                        </DimGroup>
                    </Card>
                </Col>
            </Row>
        </form>
    )
})
